/**
 * 이사 유형 속성·열거값의 한국어 라벨.
 *
 * 백엔드 스펙은 `delayReason` 처럼 영문 camelCase 를 그대로 내려줄 때가 있다. 질문
 * 제목과 리뷰 요약이 같은 사전을 봐야 화면마다 다른 이름이 뜨지 않는다.
 */

import { humanizeIntakeAttributeName } from "./intakeUiCopy";

/** 자주 등장하는 속성 key → 한국어. 스펙 `name` 이 한국어면 그쪽이 우선이다. */
export const ATTRIBUTE_KEY_KOREAN_LABELS: Record<string, string> = {
  // ── 공통 ──────────────────────────────────────────────────────────────────
  delayReason: "지연 사유",
  desiredVacateDate: "희망 비우는 날짜",
  desiredVacateTime: "희망 퇴거시간",
  desiredMoveInDate: "희망 입주일",
  desiredMoveInTime: "희망 입주 시간",
  vacateAddress: "비우는 주소",
  moveInAddress: "입주 주소",
  contactPhone: "연락처",
  contactName: "연락처 이름",
  storageStartDate: "보관 시작일",
  storageEndDate: "보관 종료일",
  storagePeriod: "보관 기간",
  hasPet: "반려동물 여부",
  petKind: "반려동물 종류",
  notes: "비고",
  memo: "메모",
  // ── 보관이사 (STORAGE) ────────────────────────────────────────────────────
  needsElectricity: "전기 필요",
  indoorStorage: "실내 보관",
  storageAddress: "보관 주소",
  storageType: "보관 유형",
  estimatedWeight: "예상 무게",
  // ── 경유이사 (STOPOVER) ───────────────────────────────────────────────────
  stopoverAddress: "경유지 주소",
  transportMethod: "운반 방법",
  floorCount: "층수",
  stopoverCargo: "경유지 화물",
  stopoverLeg: "경유 구간",
  serviceScope: "서비스 범위",
  // ── 제주/도서·장거리 양일이사 (JEJU, LONG_DISTANCE) ───────────────────────
  overnightDateRange: "숙박 날짜",
  overnightLocation: "숙박 장소",
  overnightPrevDayOk: "전일기준 양일이사 가능여부",
  overnightNextDayOk: "다음날 기준 양일이사 가능여부",
  longDistanceRoute: "장거리 경로",
  // ── 분리이사 (SPLIT) ──────────────────────────────────────────────────────
  arrivalAddress2: "도착지 주소",
  packingMethod: "포장 방법",
  movingDate: "이사일",
  splitReason: "분리 사유",
  // ── 합침이사 (MERGE) ──────────────────────────────────────────────────────
  departureAddress2: "출발지 주소",
  mergeAddress: "합침 주소",
  // ── 부분이사 (PARTIAL) ────────────────────────────────────────────────────
  partialItems: "이사 항목",
  targetRooms: "대상 방",
  // ── 사무실이사 (OFFICE) ───────────────────────────────────────────────────
  officeFloor: "사무실 층수",
  officeArea: "사무실 면적",
  serverRoomExists: "서버실 여부",
  // ── 기타 공통 ─────────────────────────────────────────────────────────────
  hasElevator: "엘리베이터 여부",
  parkingAvailable: "주차 가능 여부",
  distanceFromParking: "주차 거리",
  doorCode: "현관 비밀번호",
  specialInstructions: "특이사항",
  requiresAssembly: "조립 필요",
  accessMethod: "진입 방법",
  cargoType: "화물 유형",
  legType: "구간 유형",
  ramp: "경사로 여부",
  stairCount: "계단 수",
  entranceSpace: "진입 공간",
  weight: "무게",
  area: "면적",
  supplyArea: "공급 면적",
};

/** 스펙이 `enumDisplayNames` 를 안 내려줄 때 쓰는 열거값 폴백. */
export const ENUM_CODE_KOREAN_LABELS: Record<string, string> = {
  // ── 운반 방법 ──────────────────────────────────────────────────────────────
  ELEVATOR: "엘리베이터",
  STAIR: "계단",
  LADDER_TRUCK: "사다리차",
  FIRST_FLOOR: "1층",
  NO_ELEVATOR: "엘리베이터 없음",
  LONG_DISTANCE_CARRYING: "장거리 운반",
  // ── 서비스 범위 ────────────────────────────────────────────────────────────
  DISASSEMBLY_TRANSPORT: "분해/운반",
  DISASSEMBLY_TRANSPORT_INSTALLATION: "분해/운반/설치",
  TRANSPORT_ONLY: "운반만",
  INSTALLATION_ONLY: "설치만",
  TRANSPORT_INSTALLATION: "운반/설치",
  // ── 경유 구간 ──────────────────────────────────────────────────────────────
  DEPARTURE_TO_STOPOVER: "출발지→경유지",
  STOPOVER_TO_ARRIVAL: "경유지→도착지",
  DEPARTURE_TO_ARRIVAL: "전 구간",
  FULL_LEG: "전 구간",
  // ── 공통 열거형 ────────────────────────────────────────────────────────────
  BOTH: "전체",
  ALL: "전체",
  NONE: "없음",
  OTHER: "기타",
  YES: "예",
  NO: "아니오",
  // ── 포장 방법 (PackingMethod 및 spec enum 변형 포함) ──────────────────────
  FULL_PACKING: "완전 포장",
  PARTIAL_PACKING: "반포장",
  SELF_PACKING: "셀프 포장",
  NO_PACKING: "포장 없음",
  FULL_PACK: "완전 포장",
  HALF_PACK: "반포장",
  SELF_PACK: "셀프 포장",
  NO_PACK: "포장 없음",
  // ── 보관 유형 ──────────────────────────────────────────────────────────────
  INDOOR: "실내",
  OUTDOOR: "실외",
  CLIMATE_CONTROLLED: "온습도 관리",
  // ── 경유 구간 추가 변형 ────────────────────────────────────────────────────
  TO_STOPOVER: "→ 경유지",
  FROM_STOPOVER: "경유지 →",
  // ── 운반 방법 추가 ─────────────────────────────────────────────────────────
  SKY_LIFT: "스카이 리프트",
  // ── 지연 사유 ──────────────────────────────────────────────────────────────
  CLEANING: "입주청소",
  BETWEEN_CLEAN: "사이청소",
  RENOVATION: "공사/인테리어",
  REMODELING: "리모델링",
  SCHEDULE_CONFLICT: "일정 충돌",
  TENANT_DELAY: "전 세입자 지연",
  VACANCY_DELAY: "공실 대기",
  LOAN_DELAY: "대출 지연",
  CONTRACT_DELAY: "계약 지연",
  REGISTRATION_DELAY: "등기 지연",
  PEST_CONTROL: "방역",
  PAINTING: "도배/도색",
  FLOOR_WORK: "바닥 공사",
  // ── 이사 항목 ──────────────────────────────────────────────────────────────
  FURNITURE: "가구",
  APPLIANCES: "가전",
  BOXES: "박스",
};

export function looksLikeRawCamelCase(label: string): boolean {
  return /^[a-z][a-zA-Z0-9]*$/.test(label.trim());
}

/** 한국어가 없고 영어 단어로만 이루어진 라벨 (예: "Needs electricity"). */
export function looksLikeEnglishLabel(label: string): boolean {
  const s = label.trim();
  if (/[가-힣]/.test(s)) return false;
  return /^[A-Za-z][A-Za-z0-9\s_-]*$/.test(s);
}

/** camelCase key → 한국어 사전 우선, 없으면 읽을 수 있게 띄어 쓴다. */
export function humanizeIntakeAttributeKey(
  rawKey: string,
  fallback?: string,
): string {
  const k = String(rawKey ?? "").trim();
  if (!k) return fallback ?? "항목";
  if (ATTRIBUTE_KEY_KOREAN_LABELS[k]) return ATTRIBUTE_KEY_KOREAN_LABELS[k];
  if (/[가-힣]/.test(k)) return k;
  const words = k.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * attr.key + attr.name + kind → 한국어 라벨.
 *
 * 1. 사전에 등록된 key 2. 한국어 name 3. 영문이면 key 사전 재시도 → camelCase 풀기.
 */
export function humanizeAttrLabel(
  attrKey: string,
  attrName: string,
  kindHint: string,
): string {
  if (ATTRIBUTE_KEY_KOREAN_LABELS[attrKey])
    return ATTRIBUTE_KEY_KOREAN_LABELS[attrKey];
  const base = humanizeIntakeAttributeName(attrName, kindHint);
  if (looksLikeRawCamelCase(base) || looksLikeEnglishLabel(base)) {
    return humanizeIntakeAttributeKey(attrKey, base);
  }
  return base;
}

/** 열거 코드 → 한국어. 스펙의 `enumDisplayNames` 가 먼저다. */
export function humanizeEnumValue(
  value: string,
  enumDisplayNames?: Record<string, string>,
): string {
  return enumDisplayNames?.[value] ?? ENUM_CODE_KOREAN_LABELS[value] ?? value;
}
